export const M10_UNIT_4 = {
  id: "m10-u4-events-past",
  title: "Unit 4: Events That Changed the World",
  topic: "Past Simple Passive + Disasters & Environment",
  iconType: "globe",
  grammarImage: "/m10-u4-events-past.jpg",
  grammarText: "Unit focus: Past Simple Passive (was/were + past participle) to describe historical events, natural disasters, and environmental issues.",

  sets: [

    // ===================================================================
    // SET 1: NATURAL DISASTERS VOCABULARY (B1+)
    // ===================================================================
    {
      id: "m10-u4-set1",
      title: "Set 1: Disasters in the News",
      context: `*Monday morning - Emma's newsroom*

Emma is researching her new documentary.
She reads old newspaper headlines about natural disasters.
"Earthquake hits Japan." "Hurricane destroys the city."
These events changed everything for the people involved.

*Lunes por la mañana - Sala de noticias de Emma*

Emma investiga su nuevo documental.
Lee viejos titulares sobre desastres naturales.
"Terremoto golpea Japón." "Huracán destruye la ciudad."
Estos eventos lo cambiaron todo para las personas involucradas.`,

      questions: [
        {
          type: "match",
          q: "Match each disaster to its meaning:",
          pairs: [
            { left: "earthquake", right: "terremoto" },
            { left: "hurricane", right: "huracán" },
            { left: "flood", right: "inundación" }
          ],
          a: "completed",
          successTip: "Natural disasters! Earthquake (ground shakes), Hurricane (powerful storm), Flood (water covers land). All cause massive destruction! 🌍"
        },

        {
          type: "mc",
          q: "Headline: 'A powerful ___ hit the coast, destroying hundreds of homes.'",
          options: ["hurricane", "flood", "earthquake"],
          a: "hurricane",
          tip: "Hurricane = huracán (powerful tropical storm)."
        },

        {
          type: "mc",
          q: "Headline: 'Heavy rains caused a massive ___ in the valley.'",
          options: ["flood", "earthquake", "hurricane"],
          a: "flood",
          tip: "Flood = inundación (water overflow).",
          successTip: "Flood as verb too! 'The river flooded the town.' Same word, noun and verb! Very common in news! 📰"
        },

        {
          type: "input",
          q: "Emma reads: 'A 7.8 magnitude ___ struck the region at dawn.'",
          a: "earthquake",
          alts: ["Earthquake"],
          tip: "Earthquake = terremoto (ground shaking)."
        },

        {
          type: "match",
          q: "Match more disaster vocabulary:",
          pairs: [
            { left: "shipwreck", right: "naufragio" },
            { left: "oil spill", right: "derrame de petróleo" },
            { left: "smog", right: "niebla tóxica" }
          ],
          a: "completed"
        },

        {
          type: "mc",
          q: "Headline: 'Cargo vessel sinks — ___ feared off the coast.'",
          options: ["shipwreck", "flood", "smog"],
          a: "shipwreck",
          tip: "Shipwreck = naufragio (sunken/crashed ship).",
          successTip: "Un buque de carga se hunde; se teme por un naufragio frente a la costa"
        },

        {
          type: "mc",
          q: "Environmental report: 'Thick ___ covered the city for three days.'",
          options: ["smog", "flood", "oil spill"],
          a: "smog",
          tip: "Smog = niebla tóxica (smoke + fog mixture).",
          successTip: "Smog = smoke + fog! It's a portmanteau word. Common in cities like Beijing, Mexico City. Serious health risk! 😷"
        },

        {
          type: "mc",
          q: "News alert: 'An ___ polluted 500km of coastline.'",
          options: ["oil spill", "earthquake", "hurricane"],
          a: "oil spill",
          tip: "Oil spill = derrame de petróleo (oil in ocean)."
        },

        {
          type: "match",
          q: "Match the remaining environmental terms:",
          pairs: [
            { left: "deforestation", right: "deforestación" },
            { left: "global warming", right: "calentamiento global" },
            { left: "overpopulation", right: "sobrepoblación" }
          ],
          a: "completed",
          successTip: "These are slow disasters — they happen over years, not hours. But the damage is just as real! 🌱"
        },

        {
          type: "mc",
          q: "Documentary title: '___ — How the World Got Too Crowded.'",
          options: ["Overpopulation", "Deforestation", "Global warming"],
          a: "Overpopulation",
          tip: "Overpopulation = sobrepoblación (too many people)."
        }
      ]
    },

    // ===================================================================
    // SET 2: READING NEWS HEADLINES — PASSIVE INTRODUCED (B1+)
    // ===================================================================
    {
      id: "m10-u4-set2",
      title: "Set 2: How News Is Written",
      context: `*Monday afternoon - Journalism class*

Emma's editor explains how news headlines work.
"Notice something? We don't always say WHO did it."
"200 homes were destroyed." "The river was blocked."
In news writing, the event matters more than the cause.

*Lunes por la tarde - Clase de periodismo*

El editor de Emma explica cómo funcionan los titulares.
"¿Notan algo? No siempre decimos QUIÉN lo hizo."
"200 casas fueron destruidas." "El río fue bloqueado."
En el periodismo, el evento importa más que la causa.`,

      questions: [
        {
          type: "match",
          q: "Match the headline to its meaning:",
          pairs: [
            { left: "200 homes were destroyed", right: "algo destruyó 200 hogares" },
            { left: "The river was blocked", right: "algo bloqueó el río" },
            { left: "Three people were rescued", right: "alguien rescató a tres personas" }
          ],
          a: "completed",
          successTip: "Passive voice focuses on WHAT happened, not WHO did it. Perfect for news! The event is the star! 📺"
        },

        {
          type: "mc",
          q: "Headline: '40 families ___ from their homes after the flood.'",
          options: ["were evacuated", "evacuated", "evacuate"],
          a: "were evacuated",
          tip: "Passive = were + past participle."
        },

        {
          type: "mc",
          q: "Report: 'The old bridge ___ by the earthquake.'",
          options: ["was damaged", "damaged", "is damaged"],
          a: "was damaged",
          tip: "Past event = was + past participle.",
          successTip: "Was damaged vs Is damaged! WAS = past event (no longer happening). IS = current state. Check the timeline! ⏰"
        },

        {
          type: "input",
          q: "Emma writes: 'In a remote island, three sailors were rescued after the ______.'",
          a: "shipwreck",
          alts: ["wreck", "skining", "accident", "disaster"],
          tip: "Rescue → rescued. Passive: were rescued."
        },

        {
          type: "mc",
          q: "Breaking news: 'The city ___ before the hurricane arrived.'",
          options: ["was warned", "warned", "were warned"],
          a: "was warned",
          tip: "City (singular) = was warned."
        },

        {
          type: "mc",
          q: "Report: 'Thousands of animals ___ by the oil spill.'",
          options: ["were affected", "affected", "was affected"],
          a: "were affected",
          tip: "Thousands (plural) = were affected."
        },

        {
          type: "order",
          q: "Traduce: 'Cinco pueblos fueron destruidos por el huracán.'",
          words: ["Five", "towns", "were", "destroyed", "by", "the", "hurricane."],
          a: "Five towns were destroyed by the hurricane.",
          extra: ["destroy", "was", "for"],
          tip: "Passive with 'by' to show the cause! 'Were destroyed by the hurricane' = fueron destruidos por el huracán."
        },

        {
          type: "mc",
          q: "Headline: 'Two missing hikers ___ in the mountains yesterday.'",
          options: ["were found", "found", "was found"],
          a: "were found",
          tip: "Two hikers (plural) = were found."
        },

        {
          type: "mc",
          q: "Emma's script: 'The coastline ___ with thick oil after the spill.'",
          options: ["was covered", "covered", "were covered"],
          a: "was covered",
          tip: "Coastline (singular) = was covered.",
          successTip: "Covered in / covered with = cubierto de. 'The beach was covered in oil.' Natural collocation! 🛢️"
        },

        {
          type: "input",
          q: "Emma rewrites: 'Someone reported the disaster.' → 'The disaster ___ ___ .'",
          a: "was reported",
          alts: ["Was reported"],
          successTip: "Active → Passive! Remove the subject, make the object the star. News writing essential skill! 📝"
        }
      ]
    },

    // ===================================================================
    // SET 3: FLOODS & EARTHQUAKES (B1+)
    // ===================================================================
    {
      id: "m10-u4-set3",
      title: "Set 3: When the Earth Shook",
      context: `*Tuesday morning - Documentary research*

Emma watches archival footage of natural disasters.
"In 1906, San Francisco was hit by a massive earthquake."
"Entire neighbourhoods were flattened in minutes."
"Thousands of people were left homeless overnight."

*Martes por la mañana - Investigación del documental*

Emma ve material de archivo de desastres naturales.
"En 1906, San Francisco fue golpeada por un gran terremoto."
"Barrios enteros fueron arrasados en minutos."
"Miles de personas quedaron sin hogar de la noche a la mañana."`,

      questions: [
        {
          type: "mc",
          q: "In 1906, San Francisco ___ by a devastating earthquake.",
          options: ["was hit", "hit", "were hit"],
          a: "was hit",
          tip: "City (singular) = was hit."
        },

        {
          type: "mc",
          q: "After the earthquake, many buildings ___ completely.",
          options: ["were destroyed", "destroyed", "was destroyed"],
          a: "were destroyed",
          tip: "Buildings (plural) = were destroyed.",
          successTip:"More natural word order: “were completely destroyed.”"
        },

        {
          type: "input",
          q: "Documentary: 'Thousands of people ___ without food or water.'",
          a: "were left",
          alts: ["were left", "were stranded", "were trapped", "were stuck", "were abandoned", "were cut off", "were left stranded"],
          tip: "Leave → left. Passive: were left."
        },

        {
          type: "mc",
          q: "Report: 'The entire village ___ when the river burst its banks.'",
          options: ["was flooded", "flooded", "were flooded"],
          a: "was flooded",
          tip: "Village (singular) = was flooded."
        },

        {
          type: "match",
          q: "Match the disaster results:",
          pairs: [
            { left: "Earthquake", right: "Buildings were flattened" },
            { left: "Flood", right: "Streets were submerged" },
            { left: "Hurricane", right: "Roofs were torn off" }
          ],
          a: "completed",
          successTip: "Each disaster has its own damage! Flattened (aplanado), submerged (sumergido), torn off (arrancado). Vivid vocabulary! 💥"
        },

        {
          type: "mc",
          q: "Emma's script: 'Rescue teams ___ immediately after the earthquake.'",
          options: ["were sent", "sent", "was sent"],
          a: "were sent",
          tip: "Teams (plural) = were sent.",
          successTip: "Send → sent (irregular). 'Were sent' = passive. Rescue teams don't choose to go — they are sent! 🚒"
        },

        {
          type: "mc",
          q: "News: 'The town ___ under three metres of water after the flood.'",
          options: ["was buried", "buried", "were buried"],
          a: "was buried",
          tip: "Town (singular) = was buried."
        },

        {
          type: "order",
          q: "Traduce: 'Miles de casas fueron dañadas por las inundaciones.'",
          words: ["Thousands", "of", "homes", "were", "damaged", "by", "the", "floods."],
          a: "Thousands of homes were damaged by the floods.",
          extra: ["damage", "was", "for"],
          successTip:"Passive with 'by' to show the cause! 'Were damaged by the floods' = fueron dañadas por las inundaciones. 🌊"
        },

        {
          type: "mc",
          q: "Historical fact: 'The 2004 Indian Ocean tsunami ___ by an underwater earthquake.'",
          options: ["was caused", "caused", "were caused"],
          a: "was caused",
          tip: "Tsunami (singular) = was caused.",
          successTip: "Tsunami = Japanese word! Many disaster words come from the languages of countries where they occur. 🌊"
        },

        {
          type: "input",
          q: "Emma's voiceover: 'Survivors ___ to nearby shelters by local volunteers.'",
          a: "were taken",
          alts: ["Were taken","were taken", "were brought", "were escorted", "were transported", "were moved", "were driven", "were guided", "were led", "were evacuated"],
          tip: "Take → taken. Passive: were taken."
        }
      ]
    },

    // ===================================================================
    // SET 4: OIL SPILLS & POLLUTION (B2-)
    // ===================================================================
    {
      id: "m10-u4-set4",
      title: "Set 4: When the Ocean Turned Black",
      context: `*Tuesday afternoon - Environmental archive*

Emma researches the Deepwater Horizon oil spill of 2010.
"It was one of the worst environmental disasters in history."
"Nearly 5 million barrels of oil were released into the Gulf of Mexico."
"Entire ecosystems were poisoned for years."

*Martes por la tarde - Archivo medioambiental*

Emma investiga el derrame de petróleo Deepwater Horizon de 2010.
"Fue uno de los peores desastres medioambientales de la historia."
"Casi 5 millones de barriles de petróleo fueron liberados al Golfo de México."
"Ecosistemas enteros fueron envenenados durante años."`,

      questions: [
        {
          type: "mc",
          q: "Report: 'Nearly 5 million barrels of oil ___ into the ocean.'",
          options: ["were released", "released", "was released"],
          a: "were released",
          tip: "Barrels (plural) = were released."
        },

        {
          type: "mc",
          q: "Emma writes: 'Hundreds of kilometres of coastline ___ by the oil.'",
          options: ["were polluted", "were sent", "were drained"],
          a: "were polluted",
          tip: "Kilometres (plural) = were polluted.",
          successTip:"You can say were contaminated, were covered, were damaged... but 'were polluted' is the most natural collocation for oil spills! 🛢️"
        },

        {
          type: "input",
          q: "Documentary: 'Over a million seabirds ___ as a result of the spill.'",
          a: "were killed",
          alts: ["died", "were found dead", "were poisoned", "were contaminated", "were affected", "were coated in oil", "were harmed", "were wiped out"],
          tip: "Kill → killed. Passive: were killed.",
          successTip: "Killed” is neutral (died from any cause); “murdered” means intentionally killed by someone (a crime)."
        },

        {
          type: "mc",
          q: "Fact: 'Local fishing communities ___ for years after the disaster.'",
          options: ["were affected", "affected", "was affected"],
          a: "were affected",
          tip: "Communities (plural) = were affected."
        },

        {
          type: "mc",
          q: "Report: 'The leak ___ only after 87 days of continuous oil flow.'",
          options: ["was stopped", "stopped", "were stopped"],
          a: "was stopped",
          tip: "Leak (singular) = was stopped.",
          successTip: "87 days! The Deepwater Horizon spill really lasted that long. One of the worst in history. 🌊🛢️"
        },

        {
          type: "match",
          q: "Match the environmental impact:",
          pairs: [
            { left: "Oil spill", right: "Animals were poisoned" },
            { left: "Air pollution", right: "Cities were covered in smog" },
            { left: "Deforestation", right: "Forests were cleared" }
          ],
          a: "completed",
          successTip: "Each environmental problem has its passive consequences. Learning these = understanding global news! 🌍"
        },

        {
          type: "mc",
          q: "Emma's script: 'The clean-up operation ___ by thousands of volunteers.'",
          options: ["was led", "led", "were led"],
          a: "was led",
          tip: "Operation (singular) = was led.",
          successTip: "Lead → led (irregular). 'Was led by' = fue liderado por. Common in reports about organised responses! 👥"
        },

        {
          type: "order",
          q: "Traduce: 'Los corales fueron destruidos por la contaminación del agua.'",
          words: ["The", "corals", "were", "destroyed", "by", "water", "pollution."],
          a: "The corals were destroyed by water pollution.",
          extra: ["for", "was", "contaminated"],
          successTip: "Contamination = unwanted substance present; pollution = contamination that harms the environment."
        },

        {
          type: "mc",
          q: "News: 'Local beaches ___ to swimmers for over a year.'",
          options: ["were closed", "closed", "was closed"],
          a: "were closed",
          tip: "Beaches (plural) = were closed."
        },

        {
          type: "input",
          q: "Emma's conclusion: 'The full extent of the damage ___ understood only years later.'",
          a: "was",
          alts: ["would be", "could be", "was finally", "was fully", "came to be"],
          successTip: "extent = magnitude; 'Was understood' = se entendió/se comprendió. Formal, journalistic! 📰"
        }
      ]
    },

    // ===================================================================
    // SET 5: DEFORESTATION & GLOBAL WARMING (B2-)
    // ===================================================================
    {
      id: "m10-u4-set5",
      title: "Set 5: The Slow Disasters",
      context: `*Wednesday morning - Climate conference*

Emma attends an environmental conference.
"Every minute, an area of forest the size of a football pitch is cleared."
"Global warming was accelerated by industrial activity."
"These disasters happen slowly — but the damage is permanent."

*Miércoles por la mañana - Conferencia climática*

Emma asiste a una conferencia medioambiental.
"Cada minuto, un área de bosque del tamaño de un campo de fútbol es talada."
"El calentamiento global fue acelerado por la actividad industrial."
"Estos desastres ocurren lentamente, pero el daño es permanente."`,

      questions: [
        {
          type: "mc",
          q: "Report: 'Vast areas of the Amazon ___ to make way for farmland.'",
          options: ["were cleared", "cleared", "was clearing"],
          a: "were cleared",
          tip: "Vast areas (plural) = were cleared."
        },

        {
          type: "mc",
          q: "Fact: 'Global warming ___ by decades of industrial pollution.'",
          options: ["was accelerated", "accelerated", "were accelerated"],
          a: "was accelerated",
          tip: "Global warming (singular) = was accelerated.",
          successTip: "Accelerate = acelerar. Global warming wasn't caused overnight — it was accelerated over decades. Passive shows that! 🌡️"
        },

        {
          type: "input",
          q: "Conference speaker: 'Thousands of species ___ threatened due to habitat loss.'",
          a: "were",
          alts: ["are", "have been", "were being", "were left","will be"],
          tip: "Threaten → threatened. Passive: were threatened."
        },

        {
          type: "mc",
          q: "Environmental report: 'The rainforest ___ at an alarming rate throughout the 20th century.'",
          options: ["was destroyed", "destroyed", "were destroyed"],
          a: "was destroyed",
          tip: "Rainforest (singular) = was destroyed."
        },

        {
         type: "match",
  q: "Match each cause with its most direct consequence:",
  pairs: [
    { left: "Deforestation",    right: "Forest habitats were destroyed." },
    { left: "Global warming",   right: "Arctic ice was reduced." },
    { left: "Overpopulation",   right: "Freshwater supplies were strained."}
          ],
          a: "completed",
          successTip: "Cause → Consequence in passive! Makes connections clearer in scientific and journalistic writing! 📊"
        },

        {
          type: "mc",
          q: "News: 'Air quality ___ by heavy industrial activity in the region.'",
          options: ["was worsened", "worsened", "were worsened"],
          a: "was worsened",
          tip: "Air quality (singular) = was worsened.",
          successTip: "'Was worsened' = empeoró. You can also say 'got worse'"
        },

        {
          type: "order",
          q: "Traduce: 'Millones de árboles fueron talados para construir ciudades.'",
          words: ["Millions", "of", "trees", "were", "cut", "down", "to", "build", "cities."],
          a: "Millions of trees were cut down to build cities.",
          extra: ["cutting", "was", "by"],
          successTip: "Cut down = talar.  Common phrase in deforestation news! 🌳"
        },

        {
          type: "mc",
          q: "Conference fact: 'In the 20th century, over half of the world's forests ___.'",
          options: ["were lost", "lost", "was lost"],
          a: "were lost",
          tip: "Forests (plural) = were lost.",
          successTip: "Lose → lost (irregular). 'Were lost' = se perdieron. Notice: no 'by...' because the agent is complex/known! 🌲"
        },

        {
          type: "mc",
          q: "Emma's script: 'By 2000, many coral reefs ___ by rising ocean temperatures.'",
          options: ["were bleached", "bleached", "was bleached"],
          a: "were bleached",
          tip: "Reefs (plural) = were bleached.",
          successTip: "Bleach = blanquear/decolorar. Coral bleaching = when corals lose their colour and die due to warm water. 🪸"
        },

        {
          type: "input",
          q: "Documentary close: 'These ecosystems ___ ___ over centuries — and lost in decades.'",
          a: "were built",
          alts: ["Were built","were built", "were formed", "were shaped", "were created", "were developed", "were nurtured"],
          successTip: "Built over centuries, lost in decades. Passive makes this contrast devastating. That's the power of passive voice! 💔"
        }
      ]
    },

    // ===================================================================
    // SET 6: HISTORICAL EVENTS & SHIPWRECKS (B2-)
    // ===================================================================
    {
      id: "m10-u4-set6",
      title: "Set 6: Lost to History",
      context: `*Wednesday afternoon - History museum*

Emma visits an exhibition on historical disasters.
"The Titanic was struck by an iceberg in 1912."
"Over 1,500 people were lost at sea that night."
"For 73 years, the wreck was never found."

*Miércoles por la tarde - Museo de historia*

Emma visita una exposición sobre desastres históricos.
"El Titanic fue golpeado por un iceberg en 1912."
"Más de 1.500 personas se perdieron en el mar esa noche."
"Durante 73 años, el naufragio nunca fue encontrado."`,

      questions: [
        {
          type: "mc",
          q: "Exhibit: 'The Titanic ___ by an iceberg on April 15th, 1912.'",
          options: ["was struck", "struck", "were struck"],
          a: "was struck",
          tip: "Titanic (singular) = was struck.",
          successTip: "Strike → struck (irregular). 'Was struck by' = fue golpeado por. Common in disaster reports! 🧊"
        },

        {
          type: "mc",
          q: "Historical note: 'Over 1,500 people ___ when the ship sank.'",
          options: ["were killed", "killed", "was killed"],
          a: "were killed",
          tip: "People (plural) = were killed."
        },

        {
          type: "input",
          q: "Museum panel: 'The wreck ___ ___ by explorer Robert Ballard in 1985.' (discover → passive)",
          a: "was discovered",
          alts: ["Was discovered"],
          tip: "Discover → discovered. Passive: was discovered."
        },

        {
          type: "mc",
          q: "Exhibit: 'Pompeii ___ by the eruption of Mount Vesuvius in 79 AD.'",
          options: ["was buried", "buried", "were buried"],
          a: "was buried",
          tip: "Pompeii (singular) = was buried.",
          successTip: "Pompeii was preserved so well because it was buried quickly under ash. Disaster became history's time capsule! 🏛️"
        },

        {
          type: "match",
          q: "Match the historical events:",
          pairs: [
            { left: "The Titanic (1912)", right: "was sunk by an iceberg" },
            { left: "Pompeii (79 AD)", right: "was buried by a volcano" },
            { left: "Hiroshima (1945)", right: "was destroyed by a bomb" }
          ],
          a: "completed",
          successTip: "History in passive! We focus on what happened TO places and people, not who caused it. Powerful writing! 📜"
        },

        {
          type: "mc",
          q: "Emma's script: 'Survivors of the shipwreck ___ by passing merchant ships.'",
          options: ["were rescued", "rescued", "was rescued"],
          a: "were rescued",
          tip: "Survivors (plural) = were rescued."
        },

        {
          type: "mc",
          q: "Exhibit: 'The ancient city ___ for centuries before archaeologists found it.'",
          options: ["was forgotten", "forgot", "were forgotten"],
          a: "was forgotten",
          tip: "City (singular) = was forgotten.",
          successTip: "Forget → forgotten (irregular). 'Was forgotten' = fue olvidado. Until it was rediscovered! History loves passive! 🗿"
        },

        {
          type: "order",
          q: "Traduce: 'El barco fue hundido durante una tormenta en el Atlántico.'",
          words: ["The", "ship", "was", "sunk", "during", "a", "storm", "in", "the", "Atlantic."],
          a: "The ship was sunk during a storm in the Atlantic.",
          extra: ["sink", "sank", "at"],
          successTip: "That was a shipwreck! Wreck = any ruined vehicle/thing; shipwreck = a wrecked/sunken ship."
        },

        {
          type: "mc",
          q: "Historical fact: 'The cargo of gold ___ from the sunken ship two centuries later.'",
          options: ["was recovered", "recovered", "were recovered"],
          a: "was recovered",
          tip: "Cargo (singular/uncountable) = was recovered.",
          successTip: "Recover = recuperar. 'The cargo was recovered' = la carga fue recuperada. Common word in disaster/rescue news! 💰"
        },

        {
          type: "input",
          q: "Emma records: 'The shipwreck ___ as a protected historical site in 1980.' (declare → passive)",
          a: "was declared",
          alts: ["Was declared"],
          successTip: "Declare → declared. Official actions are almost always written in passive! 'Was declared', 'was announced', 'was signed'! 🏛️"
        }
      ]
    },

    // ===================================================================
    // SET 7: BY + AGENT (B2)
    // ===================================================================
    {
      id: "m10-u4-set7",
      title: "Set 7: Who Was Responsible?",
      context: `*Thursday morning - Investigative journalism*

Emma investigates who caused the disasters.
"Sometimes we know exactly who was responsible."
"The forest was destroyed BY illegal loggers."
"Sometimes the cause is unknown — so we don't say 'by'."

*Jueves por la mañana - Periodismo de investigación*

Emma investiga quién causó los desastres.
"A veces sabemos exactamente quién fue el responsable."
"El bosque fue destruido POR taladores ilegales."
"A veces la causa es desconocida, así que no decimos 'by'."`,

      questions: [
        {
          type: "mc",
          q: "Emma writes: 'The forest was cleared ___ illegal logging companies.'",
          options: ["by", "from", "with"],
          a: "by",
          tip: "By + agent = por + responsable.",
          successTip: "BY identifies the agent! 'Was destroyed BY' = fue destruido POR. The most important word in passive! 🔑"
        },

        {
          type: "mc",
          q: "'The oil spill was caused ___ a broken pipeline.'",
          options: ["by", "from", "of"],
          a: "by",
          tip: "By = por (the cause/agent)."
        },

        {
  type: "mc",
  q: "The cause of the fire is unknown. Which version is correct for the news report?",
  options: [
    "'The building was destroyed by someone.'",
    "'The building was destroyed.'",
    "'Someone destroyed the building.'"
  ],
  a: "'The building was destroyed.'",
  tip: "No 'by' when the agent is unknown or unimportant.",
  successTip: "Skip 'by' when you can't name the agent. 'By someone' sounds vague and unprofessional in journalism! ✍️"
},


        {
          type: "input",
          q: "Emma's report: 'The contaminated water ___ ___ by untreated factory waste.'",
          a: "was caused",
          alts: ["Was done","was polluted", "was contaminated", "was tainted", "was poisoned", "was fouled", "was heavily polluted", "was badly polluted", "was rendered unsafe", "was made toxic"],
          tip: "Was caused by = fue causado por."
        },

        {
          type: "mc",
          q: "Environmental crime: 'Hundreds of birds were killed ___ the oil spill.'",
          options: ["by", "from", "because"],
          a: "by",
          tip: "Killed by = asesinados por (the cause)."
        },

        {
          type: "mc",
          q: "News: 'Three factories were closed ___.'",
          options: ["(no 'by' needed)", "by someone", "by people"],
          a: "(no 'by' needed)",
          tip: "Unknown or unimportant agent = no 'by'.",
          successTip: "Real journalism tip! Don't add 'by' if you can't name the agent. It looks vague and unprofessional. Less is more! ✍️"
        },

        {
          type: "order",
          q: "Traduce: 'El derrame fue causado por un oleoducto roto.'",
          words: ["The", "spill", "was", "caused", "by", "a", "broken", "pipeline."],
          a: "The spill was caused by a broken pipeline.",
          extra: ["cause", "from", "for"]
        },

        {
          type: "mc",
          q: "Report: 'The contaminated land ___ a team of international scientists.'",
          options: ["was studied by", "was studied from", "studied by"],
          a: "was studied by",
          tip: "Was studied by = fue estudiado por."
        },

        {
          type: "mc",
          q: "Emma's script: 'The disaster ___ ___ government negligence, according to investigators.'",
          options: ["was caused by", "was caused from", "caused by"],
          a: "was caused by",
          tip: "Was caused by = fue causado por.",
          successTip: "'According to investigators' = según los investigadores. Classic journalistic phrase for attributing blame! 🎤"
        },

        {
          type: "input",
          q: "Breaking story: 'The deforestation ___ ___ ___ large agricultural corporations over 30 years.'",
          a: "was driven by",
          alts: ["Was driven by","was driven by", "was caused by", "was fueled by", "was pushed by", "was accelerated by", "was largely driven by", "was largely caused by"],
          successTip: "The most natural way is: 'was driven by' = fue impulsado por. Powerful word for systemic causes! 💼"
        }
      ]
    },

    // ===================================================================
    // SET 8: PASSIVE QUESTIONS (B2)
    // ===================================================================
    {
      id: "m10-u4-set8",
      title: "Set 8: Asking the Hard Questions",
      context: `*Thursday afternoon - Press conference*

Emma stands up with her microphone.
"Was the area evacuated before the flood?"
"When was the oil spill first reported?"
"How many people were affected by the disaster?"

*Jueves por la tarde - Rueda de prensa*

Emma se pone de pie con su micrófono.
"¿Fue evacuada la zona antes de la inundación?"
"¿Cuándo se reportó por primera vez el derrame de petróleo?"
"¿Cuántas personas fueron afectadas por el desastre?"`,

      questions: [
        {
          type: "match",
          q: "Match each question to its focus:",
          pairs: [
            { left: "Was the area evacuated?", right: "Yes/No question" },
            { left: "When was it reported?", right: "Time question" },
            { left: "How many were affected?", right: "Quantity question" }
          ],
          a: "completed",
          successTip: "Passive questions = Was/Were + subject + past participle? or WH + was/were + subject + past participle? 🎤"
        },

        {
          type: "mc",
          q: "Emma asks: '___ the residents warned before the hurricane?'",
          options: ["Were", "Was", "Did"],
          a: "Were",
          tip: "Residents (plural) = Were + past participle?"
        },

        {
          type: "mc",
          q: "Press conference: '___ the oil company held responsible?'",
          options: ["Was", "Were", "Did"],
          a: "Was",
          tip: "Company (singular) = Was + past participle?"
        },

        {
          type: "input",
          q: "Emma's question: '___ ___ the earthquake first detected?'",
          a: "When was",
          alts: ["when was", "Where was", "How was"],
          tip: "When was = ¿Cuándo fue...? (passive question)."
        },

        {
          type: "mc",
          q: "Hard question: '___ enough resources sent to help survivors?'",
          options: ["Were", "Was", "Are"],
          a: "Were",
          tip: "Resources (plural) = Were sent?"
        },

        {
          type: "mc",
          q: "Follow-up: '___ the disaster caused by human error or natural forces?'",
          options: ["Was", "Were", "Is"],
          a: "Was",
          tip: "Disaster (singular) = Was caused?"
        },

        {
          type: "order",
          q: "Traduce: '¿Por qué no fue reportado el derrame inmediatamente?'",
          words: ["Why", "wasn't", "the", "spill", "reported", "immediately?"],
          a: "Why wasn't the spill reported immediately?",
          extra: ["weren't", "report", "did"],
          successTip: "Negative questions are very common in real English!" 
        },
        {
          type: "mc",
          q: "Emma presses: '___ the affected families given any compensation?'",
          options: ["Were", "Was", "Did"],
          a: "Were",
          tip: "Families (plural) = Were given?",
          successTip: "Give → given (irregular). 'Were given compensation' = les dieron compensación. Perfect passive for legal/financial questions! ⚖️"
        },

        {
          type: "mc",
          q: "Response: 'Yes, several aid organisations ___ contacted immediately.'",
          options: ["were", "was", "did"],
          a: "were",
          tip: "Organisations (plural) = were contacted."
        },

        {
          type: "input",
          q: "Emma's final question: '___ ___ people were evacuated before the floods reached the town?'",
          a: "How many",
          alts: ["How many", "Were any", "Had the"],
          successTip: "How many were...? = ¿Cuántas/Cuántos fueron...? The most powerful press conference question! 🎙️"
        }
      ]
    },

    // ===================================================================
    // SET 9: PASSIVE IN NEWS REPORTS (B2+)
    // ===================================================================
    {
      id: "m10-u4-set9",
      title: "Set 9: Writing the News",
      context: `*Friday morning - Emma's newsroom deadline*

Emma writes her documentary script under pressure.
Every sentence must be precise and professional.
"Passive voice isn't just grammar — it's how serious journalism sounds."

*Viernes por la mañana - Cierre de redacción de Emma*

Emma escribe su guión del documental bajo presión.
Cada oración debe ser precisa y profesional.
"La voz pasiva no es solo gramática, es como suena el periodismo serio."`,

      questions: [
        {
          type: "mc",
          q: "Opening line: 'In March 1989, the Exxon Valdez ran aground ___ Alaska, releasing millions of litres of oil.'",
          options: ["on", "in", "for"],
          a: "in",
          tip: "Sudden completed past event = Past Simple.",
          successTip: "Run aground = encallar (ship hits ground). Not all sentences need passive — mix active and passive for strong writing! ✍️"
        },

        {
          type: "mc",
          q: "Follow-up: 'Over 2,000 kilometres of coastline ___ by the spill.'",
          options: ["were affected", "affected", "was affecting"],
          a: "were affected",
          tip: "Kilometres (plural) = were affected."
        },

        {
          
  type: "order",
  q: "Traduce: 'Más de 250,000 aves marinas fueron asesinadas como resultado directo.'",
  words: ["More", "than", "250,000", "seabirds", "were", "killed", "as", "a", "direct", "result."],
  a: "More than 250,000 seabirds were killed as a direct result.",
  extra: ["murdered", "was", "died"],
  successTip: "Killed = neutral (died from any cause); murdered = intentionally killed by someone (a crime). In news, 'were killed' is the most common and neutral way to report disaster casualties! 🐦"
        },

        {
          type: "mc",
          q: "Emma's script: 'The company ___ responsible and ordered to pay billions in damages.'",
          options: ["was held", "held", "were held"],
          a: "was held",
          tip: "Company (singular) = was held responsible.",
          successTip: "Held responsible = responsabilizado. 'Hold someone responsible' = culpar. Common in legal journalism! ⚖️"
        },

        {
          type: "mc",
          q: "Documentary line: 'New environmental laws ___ as a direct result of the disaster.'",
          options: ["were introduced", "introduced", "was introduced"],
          a: "were introduced",
          tip: "Laws (plural) = were introduced.",
          successTip: "Introduce laws = introducir leyes. Disasters often lead to new legislation. Passive shows the historical impact! 📜"
        },

        {
          type: "order",
          q: "Traduce: 'La zona costera fue declarada zona de desastre por el gobierno.'",
          words: ["The", "coastal", "area", "was", "declared", "a", "disaster", "zone", "by", "the", "government."],
          a: "The coastal area was declared a disaster zone by the government.",
        },

        {
          type: "mc",
          q: "Emma's closing line: 'Lessons ___ , but the environmental scars remain to this day.'",
          options: ["were learned", "learned", "was learned"],
          a: "were learned",
          tip: "Lessons (plural) = were learned.",
          successTip: "Lessons were learned = se aprendieron lecciones. Common closing phrase in investigative journalism. Powerful! 📺"
        },

        {
          type: "mc",
          q: "Script note: 'The clean-up operation ___ only partially successful by most experts.'",
          options: ["was considered", "considered", "were considered"],
          a: "was considered",
          tip: "Operation (singular) = was considered."
        },

        {
          type: "mc",
          q: "Emma adds: 'Many of the affected areas ___ never fully ___.'",
          options: ["were / restored", "was / grounded", "were / sunk"],
          a: "were / restored",
          tip: "restored = restaurado, grounded = encallado, sunk = hundido ."
        },

       {
  type: "order",
  q: "Traduce: 'El verdadero costo del desastre nunca fue completamente comprendido por las generaciones futuras.'",
  words: ["The", "true", "cost", "of", "the", "disaster", "was", "never", "fully", "understood", "by", "future", "generations."],
  a: "The true cost of the disaster was never fully understood by future generations.",
  successTip: "In passive forms, put 'never' after the auxiliary (was) and 'fully' before the past participle: “was never fully understood."
        }
      ]
    },

    // ===================================================================
    // SET 10: ACTIVE VS PASSIVE — FULL CONTRAST (B2+)
    // ===================================================================
    {
      id: "m10-u4-set10",
      title: "Set 10: Telling the Complete Story",
      context: `*Friday evening - Documentary premiere*

Emma's documentary is ready to screen.
She used both active and passive perfectly throughout.
"Active = who did it. Passive = what happened to it."
"Together, they tell the complete story."

*Viernes por la noche - Estreno del documental*

El documental de Emma está listo para proyectarse.
Usó perfectamente tanto la voz activa como la pasiva.
"Activa = quién lo hizo. Pasiva = qué le pasó."
"Juntas, cuentan la historia completa."`,

      questions: [
        {
          type: "match",
          q: "Match each sentence to its voice:",
          pairs: [
            { left: "The storm destroyed the village.", right: "Active" },
            { left: "The village was destroyed.", right: "Passive" },
            { left: "The bridge was built by the engineers.", right: "Passive with knwown author" }
          ],
          a: "completed",
          successTip: "The choice is yours! Active = focus on who acts. Passive = focus on what is affected. Both are correct! 🎯"
        },

        {
          type: "mc",
          q: "Which sounds more like a news headline?",
          options: [
            "'The hurricane destroyed 500 homes.'",
            "'500 homes were destroyed by the hurricane.'",
            "Both work equally well."
          ],
          a: "Both work equally well.",
          tip: "Both are used in news! Active = dramatic. Passive = formal.",
          successTip: "News uses BOTH! Active for drama, passive for formality. Great writers switch between them! 📰✍️"
        },

        {
          type: "mc",
          q: "Emma's documentary: 'In 1986, a reactor at Chernobyl ___ , and radiation ___ across Europe.'",
          options: ["exploded / was spread", "was exploded / spread", "exploded / spread"],
          a: "exploded / was spread",
          tip: "Reactor exploded (active, it happened). Radiation was spread (passive, it was carried)."
        },

        {
  type: "mc",
  q: "Emma's editor marks her script in red: 'The earthquake was happened in 2010.' ❌ How should Emma fix it?",
  options: [
    "'The earthquake occurred in 2010.'",
    "'The earthquake was occurred in 2010.'",
    "'The earthquake was taking place in 2010.'"
  ],
  a: "'The earthquake occurred in 2010.'",
  tip: "Happen/occur no necesitan pasiva — el evento ya es el sujeto.",
  successTip: "'The earthquake happened.' / 'The earthquake occurred.' — estos verbos ya describen el evento directamente. No necesitan 'was'! ✅"
        },


        {
          type: "order",
          q: "Traduce: 'El petróleo fue derramado y dañó miles de kilómetros de costa.'",
          words: ["The", "oil", "was", "spilled", "and", "damaged", "thousands", "of", "kilometres", "of", "coastline."],
          a: "The oil was spilled and damaged thousands of kilometres of coastline.",
        },

        {
          type: "mc",
          q: "Emma's closing: 'Humans ___ the planet, and one day the planet may return the favour.'",
          options: ["have damaged", "were damaged", "was damaged"],
          a: "have damaged",
          tip: "Humans as active agents = active voice.",
          successTip: "Powerful ending! Active 'humans damaged' = direct blame. Sometimes active hits harder than passive! 🌍💔"
        },

        {
          type: "mc",
          q: "Review mix: 'The report ___ by scientists, and its findings ___ worldwide.'",
          options: ["was written / were shared", "wrote / was shared", "was written / shared"],
          a: "was written / were shared",
          tip: "Report (singular) = was written. Findings (plural) = were shared.",
          successTip: "Most natural: say “around the world” (instead of “worldwide”)! 🌍✍️"
        },

        {
          type: "mc",
          q: "Which correctly transforms 'They discovered oil in the region'?",
          options: [
            "'Oil was discovered in the region.'",
            "'Oil discovered in the region.'",
            "'Oil were discovered in the region.'"
          ],
          a: "'Oil was discovered in the region.'",
          tip: "Oil (singular/uncountable) = was discovered."
        },

        {
          type: "order",
          q: "Traduce: 'El documental fue visto por millones de personas en todo el mundo.'",
          words: ["The", "documentary", "was", "watched", "by", "millions", "of", "people", "around", "the", "world."],
          a: "The documentary was watched by millions of people around the world.",
          extra: ["see", "were", "persons"],
          successTip:"“Persons” isn't wrong, but it sounds formal/legal, so here the natural phrasing is “millions of people” (not “persons”)."
        },

        {
          type: "mc",
          q: "Final question: 'The world ___ forever by these disasters — and the story ___ yet.'",
          options: ["was changed / isn't over", "changed / wasn't over", "was changed / wasn't over"],
          a: "was changed / isn't over",
          tip: "Was changed = past passive. Isn't over = present state.",
          successTip: "🏆 UNIT 4 COMPLETE! Past Simple Passive MASTERED! You write like a journalist and think like a documentarian! 🎬🌍 Unit 5 next!"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET (B2)
  // ===================================================================
  readingSet: {
    id: "m10-u4-reading",
    title: "Reading: Disasters That Shaped the World",
    category: "reading",

    questions: [
      {
        type: "passage",
        passage: `**Emma's Documentary Script — Chapter One: The Day the Ocean Turned Black**

On April 20th, 2010, an explosion rocked the Deepwater Horizon oil rig in the Gulf of Mexico. Eleven workers were killed instantly. The platform was abandoned, and within hours, it sank to the bottom of the ocean.

What followed was the largest marine oil spill in history. Over the next 87 days, nearly five million barrels of oil were released into the Gulf. Hundreds of kilometres of coastline were covered in thick, black crude oil. More than 400 species of animals were affected, including dolphins, sea turtles, and thousands of seabirds.

Local fishing communities were devastated. Their livelihoods were destroyed almost overnight. Many were not fully compensated for years.`
      },

      {
        type: "mc",
        q: "When did the Deepwater Horizon explosion happen?",
        options: ["April 20th, 2010", "April 20th, 2001", "March 20th, 2010"],
        a: "April 20th, 2010",
        tip: "Check the opening sentence."
      },

      {
        type: "mc",
        q: "How many workers were killed in the explosion?",
        options: ["Eleven", "Twenty", "Five"],
        a: "Eleven",
        tip: "'Eleven workers were killed instantly.'"
      },

      {
        type: "mc",
        q: "How long did the oil flow into the ocean?",
        options: ["87 days", "45 days", "One year"],
        a: "87 days",
        tip: "'Over the next 87 days...'"
      },

      {
        type: "mc",
        q: "Which groups were affected according to the text?",
        options: [
          "Animals and fishing communities",
          "Only animals",
          "Only local businesses"
        ],
        a: "Animals and fishing communities",
        tip: "Both are mentioned in the passage."
      },

      {
        type: "passage",
        passage: `**Emma's Documentary Script — Chapter Two: The Slow Disaster**

While the Deepwater Horizon disaster was sudden and visible, other environmental catastrophes develop slowly and silently. Deforestation is one of them.

Over the course of the 20th century, vast areas of tropical forest were cleared to make way for agriculture, logging, and urban development. It is estimated that more than half of the world's original forests have now been lost.

The consequences were severe. Thousands of species were pushed to the edge of extinction. Indigenous communities were displaced from lands their ancestors had lived on for centuries. And the global climate was altered, as forests that once absorbed carbon dioxide were replaced by farmland that released it.

"We didn't lose these forests all at once," Emma says in her voiceover. "They were taken from us, one tree at a time."`
      },

      {
        type: "mc",
        q: "What is the main subject of Chapter Two?",
        options: ["Deforestation", "Oil spills", "Hurricanes"],
        a: "Deforestation",
        tip: "The chapter focuses on forest loss."
      },

      {
        type: "mc",
        q: "Why were forests cleared?",
        options: [
          "For agriculture, logging and urban development",
          "Due to natural disasters",
          "Because of overpopulation"
        ],
        a: "For agriculture, logging and urban development",
        tip: "'cleared to make way for agriculture, logging, and urban development.'"
      },

      {
        type: "mc",
        q: "Who was displaced according to the text?",
        options: ["Indigenous communities", "Fishing communities", "Scientists"],
        a: "Indigenous communities",
        tip: "'Indigenous communities were displaced...'"
      },

      {
        type: "mc",
        q: "How does Emma describe the process of deforestation?",
        options: [
          "'One tree at a time'",
          "'All at once, overnight'",
          "'Faster than anyone expected'"
        ],
        a: "'One tree at a time'",
        tip: "Check Emma's closing voiceover.",
        successTip: "Emma's line is powerful because it uses the passive to show gradual, collective destruction. That's the art of documentary writing! 🎬"
      }
    ]
  },
readingSet: {
  id: "m10-u4-reading",
  title: "Reading: Disasters That Shaped the World",
  category: "reading",

  questions: [

    // --- PASSAGE 1 ---
    {
      type: "passage",
      passage: `**Emma's Documentary Script — Chapter One: The Explosion**

On April 20th, 2010, an explosion rocked the Deepwater Horizon oil rig in the Gulf of Mexico. Eleven workers were killed instantly. The platform was abandoned, and within hours, it sank to the bottom of the ocean.`
    },
    {
      type: "mc",
      q: "When did the Deepwater Horizon explosion happen?",
      options: ["April 20th, 2010", "April 20th, 2001", "March 20th, 2010"],
      a: "April 20th, 2010",
      tip: "Check the opening sentence."
    },
    {
      type: "mc",
      q: "What happened to the platform?",
      options: ["It was abandoned and sank", "It was repaired immediately", "It was rescued by the coastguard"],
      a: "It was abandoned and sank",
      tip: "'The platform was abandoned... it sank.'"
    },

    // --- PASSAGE 2 ---
    {
      type: "passage",
      passage: `**Chapter One: The Spill**

What followed was the largest marine oil spill in history. Over the next 87 days, nearly five million barrels of oil were released into the Gulf of Mexico. Hundreds of kilometres of coastline were covered in thick, black crude oil.`
    },
    {
      type: "mc",
      q: "How long did the oil flow into the ocean?",
      options: ["87 days", "45 days", "One year"],
      a: "87 days",
      tip: "'Over the next 87 days...'"
    },
    {
      type: "mc",
      q: "How is the oil described in the text?",
      options: ["Thick and black", "Clear and light", "Dark and thin"],
      a: "Thick and black",
      tip: "'thick, black crude oil.'"
    },

    // --- PASSAGE 3 ---
    {
      type: "passage",
      passage: `**Chapter One: The Victims**

More than 400 species of animals were affected, including dolphins, sea turtles, and thousands of seabirds. Local fishing communities were devastated. Their livelihoods were destroyed almost overnight. Many were not fully compensated for years.`
    },
    {
      type: "mc",
      q: "How many species were affected?",
      options: ["More than 400", "More than 40", "More than 4,000"],
      a: "More than 400",
      tip: "'More than 400 species were affected.'"
    },
    {
      type: "mc",
      q: "Which groups were affected according to the text?",
      options: ["Animals and fishing communities", "Only animals", "Only local businesses"],
      a: "Animals and fishing communities",
      tip: "Both are mentioned in the passage."
    },

    // --- PASSAGE 4 ---
    {
      type: "passage",
      passage: `**Chapter Two: The Slow Disaster**

While the Deepwater Horizon disaster was sudden and visible, other environmental catastrophes develop slowly and silently. Deforestation is one of them.

Over the course of the 20th century, vast areas of tropical forest were cleared to make way for agriculture, logging, and urban development. It is estimated that more than half of the world's original forests have now been lost.`
    },
    {
      type: "mc",
      q: "How is deforestation different from the oil spill?",
      options: ["It develops slowly and silently", "It is more visible", "It happened in one day"],
      a: "It develops slowly and silently",
      tip: "'other environmental catastrophes develop slowly and silently.'"
    },
    {
      type: "mc",
      q: "Why were forests cleared?",
      options: [
        "For agriculture, logging and urban development",
        "Due to natural disasters",
        "Because of overpopulation"
      ],
      a: "For agriculture, logging and urban development",
      tip: "'cleared to make way for agriculture, logging, and urban development.'"
    },

    // --- PASSAGE 5 ---
    {
      type: "passage",
      passage: `**Chapter Two: The Consequences**

The consequences were severe. Thousands of species were pushed to the edge of extinction. Indigenous communities were displaced from lands their ancestors had lived on for centuries. And the global climate was altered, as forests that once absorbed carbon dioxide were replaced by farmland that released it.

"We didn't lose these forests all at once," Emma says in her voiceover. "They were taken from us, one tree at a time."`
    },
    {
      type: "mc",
      q: "Who was displaced from their lands?",
      options: ["Indigenous communities", "Fishing communities", "Scientists"],
      a: "Indigenous communities",
      tip: "'Indigenous communities were displaced...'"
    },
    {
      type: "mc",
      q: "How does Emma describe the process of deforestation?",
      options: ["'One tree at a time'", "'All at once, overnight'", "'Faster than anyone expected'"],
      a: "'One tree at a time'",
      tip: "Check Emma's closing voiceover.",
      successTip: "Emma's line is powerful because passive voice shows gradual, collective destruction. That's the art of documentary writing! 🎬"
    }
  ]
},

  // ===================================================================
  // REVIEW SET (B2+)
  // ===================================================================
  reviewSet: {
    id: "m10-u4-review",
    title: "Unit 4 Review: The Passive Reporter",
    category: "review",

    questions: [
      {
        type: "mc",
        q: "Review: The city ___ by the earthquake.",
        options: ["was destroyed", "destroyed", "were destroyed"],
        a: "was destroyed",
        tip: "City (singular) = was destroyed."
      },

      {
        type: "mc",
        q: "Review: Thousands of animals ___ by the oil spill.",
        options: ["were affected", "affected", "was affected"],
        a: "were affected",
        tip: "Animals (plural) = were affected."
      },

      {
        type: "mc",
        q: "Review: The forest was cleared ___ logging companies.",
        options: ["by", "from", "with"],
        a: "by",
        tip: "By + agent = por + responsable."
      },

      {
        type: "mc",
        q: "Review: ___ the residents warned before the flood?",
        options: ["Were", "Was", "Did"],
        a: "Were",
        tip: "Passive question: Were + plural + past participle?"
      },

      {
        type: "mc",
        q: "Review: The Titanic ___ by an iceberg in 1912.",
        options: ["was struck", "struck", "were struck"],
        a: "was struck",
        tip: "Titanic (singular) = was struck."
      },

      {
        type: "mc",
        q: "Review: New environmental laws ___ after the disaster.",
        options: ["were introduced", "introduced", "was introduced"],
        a: "were introduced",
        tip: "Laws (plural) = were introduced."
      },

      {
        type: "mc",
        q: "Review: 'The earthquake ___ in the region.' Which is correct?",
        options: ["occurred", "was occurred", "were occurred"],
        a: "occurred",
        tip: "Occur/happen = can't be passive!"
      },

      {
        type: "mc",
        q: "Review: Three survivors ___ from the shipwreck.",
        options: ["were rescued", "rescued", "was rescued"],
        a: "were rescued",
        tip: "Survivors (plural) = were rescued."
      },

      {
        type: "mc",
        q: "Review: The oil spill ___ by a broken pipeline.",
        options: ["was caused", "caused", "were caused"],
        a: "was caused",
        tip: "Spill (singular) = was caused."
      },

      {
        type: "mc",
        q: "Review: The documentary ___ by millions of people worldwide.",
        options: ["was watched", "watched", "were watched"],
        a: "was watched",
        tip: "Documentary (singular) = was watched.",
        successTip: "🎉 UNIT 4 MASTERED! Past Passive ✅ Disaster vocabulary ✅ By + agent ✅ You write like a documentary journalist! 🎬🌍"
      }
    ]
  }
};
